/**
 * Types for Activity management in admin dashboard
 */

import type { ActivityImageValue, UploadedImage } from './upload';

// ============================================
// Form Input Types
// ============================================

export interface ActivityStep {
  text: string;
  emphasis: string;
  /** Client-side only ID for drag-and-drop tracking. Not persisted to database. */
  id?: string;
}

export interface ActivityTip {
  text: string;
  boldText?: string;
  /** Client-side only ID for drag-and-drop tracking. Not persisted to database. */
  id?: string;
}

export interface ActivityWhyItWorks {
  text: string;
  boldKeywords: string[];
}

export interface CreateActivityFormData {
  title: string;
  description: string;
  image?: ActivityImageValue | UploadedImage | null;
  documentId?: number | null;
  predefinedActivityImageId?: number | null;
  imageType?: 'custom' | 'category' | 'predefined' | null;
  duration: number | '';
  playType: 'solo_play' | 'play_together' | '';
  prepNeeded: 'no_prep_needed' | 'prep_needed' | '';
  steps: ActivityStep[];
  tips: ActivityTip[];
  whyItWorks: ActivityWhyItWorks;
  tagIds: number[];
  transitionIds: number[];
  ageGroupKeys: string[];
  categoryId: number | null;
}

/**
 * Form data for updating an activity
 * Uses the same structure as creation
 */
export type UpdateActivityFormData = CreateActivityFormData;

// ============================================
// API Response Types
// ============================================

export interface Tag {
  id: string;
  label: string;
}

export interface TagsResponse {
  success: true;
  data: Tag[];
  message: string;
  timestamp: string;
}

export interface Transition {
  id: string;
  name: string;
  label: string;
}

export interface TransitionsResponse {
  success: true;
  data: Transition[];
  message: string;
  timestamp: string;
}

export interface DevelopmentArea {
  id: string;
  label: string;
  key: string;
}

export interface DevelopmentAreasResponse {
  success: true;
  data: DevelopmentArea[];
  message: string;
  timestamp: string;
}

export interface ActivityCategory {
  id: string;
  name: string;
  key: string;
  sortOrder: number;
  color: string | null;
  imageName: string | null;
}

export interface ActivityResponse {
  id: string;
  title: string;
  description: string;
  documentId: number | null;
  predefinedActivityImageId?: number | null;
  imageType?: 'custom' | 'category' | 'predefined' | null;
  duration: number;
  playType: string;
  prepNeeded: string;
  playTypeText: string;
  prepNeededText: string;
  steps: ActivityStep[];
  tips: ActivityTip[];
  whyItWorks: ActivityWhyItWorks;
  status: string;
  tags: Tag[];
  transitions: Transition[];
  ageRanges: { fromAge: number; toAge: number }[];
  category: ActivityCategory | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateActivityResponse {
  success: true;
  data: ActivityResponse;
  message: string;
  timestamp: string;
}

// ============================================
// Dropdown Options
// ============================================

export const AGE_GROUP_OPTIONS = [
  { key: '2-3', fromAge: 2, toAge: 3, label: '2–3' },
  { key: '3-4', fromAge: 3, toAge: 4, label: '3–4' },
  { key: '4-5', fromAge: 4, toAge: 5, label: '4–5' },
] as const;
export type AgeGroupKey = '2-3' | '3-4' | '4-5';

export const PLAY_TYPE_OPTIONS = [
  { value: 'solo_play', label: 'Solo Play' },
  { value: 'play_together', label: 'Play together' },
] as const;

export const PREP_NEEDED_OPTIONS = [
  { value: 'no_prep_needed', label: 'No prep needed' },
  { value: 'prep_needed', label: 'Prep needed' },
] as const;

// ============================================
// Listing Types
// ============================================

export type ActivityStatus = 'draft' | 'published' | 'archived';
export type PlayType = 'solo_play' | 'play_together';
export type PrepNeeded = 'no_prep_needed' | 'prep_needed';

/**
 * Filters for activities listing
 */
export interface ActivityFilters {
  search: string;
  status: ActivityStatus | '';
  playType: PlayType | '';
  prepNeeded: PrepNeeded | '';
  duration: number | ''; // 10, 15, 30, 31 for 30+, or empty string for no filter
  tagIds: number[]; // Array of tag IDs for multi-select filtering
  transitionIds: number[]; // Array of transition IDs for multi-select filtering
  ageGroupKeys: string[]; // Age group filter keys
  categoryIds: number[]; // Array of category IDs for multi-select filtering
  isAiGenerated: boolean;
  generationJobId: string; // URL-only — not persisted to localStorage
  page: number;
  pageSize: number;
  sortBy: 'title' | 'status' | 'playType' | 'prepNeeded' | 'duration' | 'createdAt';
  sortOrder: 'asc' | 'desc';
}

/**
 * Activity for listing (includes tags)
 */
export interface AdminActivity {
  id: string;
  title: string;
  description: string;
  documentId: number | null;
  predefinedActivityImageId?: number | null;
  duration: number;
  playType: string;
  prepNeeded: string;
  status: string;
  tags: Tag[];
  transitions: Transition[];
  ageRanges: { fromAge: number; toAge: number }[];
  category: ActivityCategory | null;
  createdAt: string;
  updatedAt: string;
  isAiGenerated: boolean;
}

/**
 * Lightweight activity type for the schedule modal's "Add Activities" panel.
 */
export interface ModalActivity {
  id: string;
  title: string;
  transitions: Transition[];
  ageRanges: { fromAge: number; toAge: number }[];
}

/**
 * Activities listing response
 */
export interface ActivitiesListResponse {
  items: AdminActivity[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

/**
 * Complete activity details (includes steps, tips, whyItWorks)
 */
export interface ActivityDetails extends AdminActivity {
  playTypeText: string;
  prepNeededText: string;
  steps: ActivityStep[];
  tips: ActivityTip[];
  whyItWorks: ActivityWhyItWorks;
  imageUrl: string | null;
  imageType?: 'custom' | 'category' | 'predefined' | null;
  packName: { id: string; name: string } | null;
  activityKey: string | null;
}

/**
 * API response for single activity details
 */
export interface ActivityDetailsResponse {
  success: true;
  data: ActivityDetails;
  message: string;
  timestamp: string;
}

/**
 * Request body for updating activity status
 */
export interface UpdateActivityStatusRequest {
  status: ActivityStatus;
}

/**
 * API response for status update
 */
export interface UpdateActivityStatusResponse {
  success: true;
  data: ActivityDetails;
  message: string;
  timestamp: string;
}

/**
 * Activity status options
 */
export const ACTIVITY_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
] as const;

/**
 * Duration filter options
 */
export const DURATION_OPTIONS = [
  { value: 10, label: 'Up to 10 min', range: '≤ 10' },
  { value: 15, label: '10-15 min', range: '10-15' },
  { value: 30, label: '15-30 min', range: '15-30' },
  { value: 31, label: '30+ min', range: '≥ 30' },
] as const;
